import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Eye, ChalkboardTeacher, User, IdentificationBadge, X, ClockCounterClockwise, ChartBar } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import PhotoUploader from "@/components/PhotoUploader";
import EmployeePicker from "@/components/EmployeePicker";
import ToolboxMonthlyDialog from "@/components/hse/ToolboxMonthlyDialog";

const emptyForm = () => ({
  date: "", topic: "", presenter: "", area: "", duration_min: 5, project_id: defaultProject(), project_id: defaultProject(),
  key_points: "", attendees: [], photos: [],
});

/**
 * ToolboxModule — Charlas de 5 minutos.
 *
 * Antes solo permitía crear y borrar; el usuario reportó que no podía
 * ver ni editar las capturas. Ahora cada tarjeta abre un DetailDialog
 * (con TODAS las fotos, asistentes y puntos clave) y un botón "Editar"
 * que reusa el mismo formulario en modo update (PUT /hse/toolbox/{id}).
 */
export default function ToolboxModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ mode: "closed", form: emptyForm(), id: null });
  const [reportOpen, setReportOpen] = useState(false);
  // mode: "closed" | "create" | "edit" | "view"
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/hse/toolbox").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDialog({ mode: "create", form: emptyForm(), id: null });
  const openEdit = (it) => setDialog({
    mode: "edit",
    id: it.id,
    form: {
      date: it.date || "", topic: it.topic || "", presenter: it.presenter || "",
      area: it.area || "", duration_min: it.duration_min || 5,
      key_points: it.key_points || "",
      attendees: it.attendees || [], photos: it.photos || [],
    },
  });
  const openView = (it) => setDialog({ mode: "view", id: it.id, form: it });
  const close = () => setDialog({ mode: "closed", form: emptyForm(), id: null });

  const save = async () => {
    try {
      const payload = dialog.form;
      if (!assertProject(payload.project_id)) return;
      if (dialog.mode === "edit" && dialog.id) {
        await api.put(`/hse/toolbox/${dialog.id}`, payload);
        toast.success("Charla actualizada");
      } else {
        await api.post("/hse/toolbox", payload);
        toast.success("Charla registrada");
      }
      close();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id, topic) => {
    if (!window.confirm(`¿Eliminar charla "${topic}"?`)) return;
    try { await api.delete(`/hse/toolbox/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const patchForm = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const setAttendees = (attendees) => patchForm({ attendees });

  const addAtt = () => setAttendees([...(dialog.form.attendees || []), {
    name: "", id_number: "", position: "", signed: true, employee_id: "",
  }]);
  const linkEmp = (idx, emp) => {
    const n = [...dialog.form.attendees];
    n[idx] = {
      ...n[idx],
      employee_id: emp.id,
      name: emp.name || n[idx].name,
      id_number: emp.dni || n[idx].id_number,
      position: emp.position || n[idx].position,
    };
    setAttendees(n);
  };
  const unlinkEmp = (idx) => {
    const n = [...dialog.form.attendees];
    n[idx] = { ...n[idx], employee_id: "" };
    setAttendees(n);
  };

  const isFormMode = dialog.mode === "create" || dialog.mode === "edit";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{items.length} charlas registradas</p>
        <div className="flex items-center gap-2">
          <Button data-testid="toolbox-report-button" onClick={() => setReportOpen(true)} variant="outline"
                  className="gap-2 border-blue-900 text-blue-900 hover:bg-blue-50">
            <ChartBar size={14} weight="bold" /> Reporte mensual
          </Button>
          {canWrite && (
            <Button data-testid="toolbox-new-button" onClick={openCreate}
                     className="bg-blue-900 hover:bg-blue-800 gap-2">
              <Plus size={14} weight="bold" /> Nueva charla 5-min
            </Button>
          )}
        </div>
      </div>

      <ToolboxMonthlyDialog open={reportOpen} onClose={() => setReportOpen(false)} />

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-lg">
            Sin charlas registradas
          </p>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`toolbox-card-${it.id}`}
                className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3 hover:border-blue-300 transition-colors">
            <ChalkboardTeacher size={22} weight="duotone" className="text-blue-900 shrink-0 mt-1" />
            <button onClick={() => openView(it)} className="flex-1 min-w-0 text-left"
                     data-testid={`toolbox-view-${it.id}`}>
              <p className="font-heading font-bold text-slate-900 text-sm">{it.topic} <ProjectBadge id={it.project_id} className="ml-1 align-middle" /></p>
              <p className="text-xs text-slate-500">
                {it.date} · {it.presenter} · {it.area} · {it.duration_min} min
              </p>
              {it.key_points && (
                <p className="text-xs text-slate-700 mt-1 line-clamp-2">{it.key_points}</p>
              )}
              {(it.photos || []).length > 0 && (
                <div className="flex gap-1 mt-1" data-testid={`toolbox-photos-${it.id}`}>
                  {(it.photos || []).slice(0, 4).map((src, i) => (
                    <img key={i} src={src} alt="" className="w-12 h-12 rounded object-cover border border-slate-200" />
                  ))}
                  {(it.photos || []).length > 4 && (
                    <span className="text-[10px] text-slate-500 self-center ml-1">
                      +{(it.photos || []).length - 4}
                    </span>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <User size={10} /> {(it.attendees || []).length} asistentes: {(it.attendees || []).slice(0, 5).map((a) => a.name).join(", ")}
                {(it.attendees || []).length > 5 ? "…" : ""}
              </p>
            </button>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => openView(it)} data-testid={`toolbox-view-btn-${it.id}`}
                       className="text-slate-400 hover:text-blue-700 p-1" title="Ver detalle">
                <Eye size={15} />
              </button>
              {canWrite && (
                <>
                  <button onClick={() => openEdit(it)} data-testid={`toolbox-edit-${it.id}`}
                           className="text-slate-400 hover:text-blue-700 p-1" title="Editar">
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={() => remove(it.id, it.topic)} data-testid={`toolbox-delete-${it.id}`}
                           className="text-slate-400 hover:text-red-600 p-1" title="Eliminar">
                    <Trash size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Dialog unificado — modo view | create | edit */}
      <Dialog open={dialog.mode !== "closed"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto"
                        aria-describedby="toolbox-dlg-desc">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ChalkboardTeacher size={20} weight="duotone" className="text-blue-900" />
              {dialog.mode === "view" && `Charla · ${dialog.form.topic || ""}`}
              {dialog.mode === "create" && "Nueva charla de 5 minutos"}
              {dialog.mode === "edit" && "Editar charla"}
            </DialogTitle>
            <p id="toolbox-dlg-desc" className="text-xs text-slate-500">
              {dialog.mode === "view"
                ? "Detalle completo — usa Editar para modificar."
                : "Fotos, asistentes y puntos clave de la charla."}
            </p>
          </DialogHeader>

          {dialog.mode === "view" ? (
            <ToolboxDetailView it={dialog.form} onEdit={canWrite ? () => openEdit(dialog.form) : null} />
          ) : (
            <ToolboxForm
              form={dialog.form} patchForm={patchForm}
              addAtt={addAtt} linkEmp={linkEmp} unlinkEmp={unlinkEmp}
              save={save} mode={dialog.mode}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolboxDetailView({ it, onEdit }) {
  return (
    <div className="space-y-4 text-sm" data-testid="toolbox-detail-view">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fecha" value={it.date} testid="detail-date" />
        <Field label="Duración" value={`${it.duration_min || 0} min`} />
        <Field label="Presentador" value={it.presenter} />
        <Field label="Área" value={it.area} />
      </div>
      {it.key_points && (
        <div>
          <Label className="overline-label">Puntos clave</Label>
          <p className="mt-1 text-slate-800 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3">
            {it.key_points}
          </p>
        </div>
      )}
      {(it.photos || []).length > 0 && (
        <div data-testid="detail-photos">
          <Label className="overline-label">Fotos ({it.photos.length})</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {it.photos.map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noreferrer" className="block">
                <img src={src} alt="" className="w-full h-32 object-cover rounded border border-slate-200 hover:border-blue-400 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      )}
      <div>
        <Label className="overline-label">Asistentes ({(it.attendees || []).length})</Label>
        <div className="mt-1 border border-slate-200 rounded divide-y divide-slate-100 max-h-56 overflow-y-auto">
          {(it.attendees || []).length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Sin asistentes registrados</p>
          )}
          {(it.attendees || []).map((a, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-2 text-xs"
                  data-testid={`detail-att-${idx}`}>
              {a.employee_id
                ? <IdentificationBadge size={14} weight="fill" className="text-emerald-700 shrink-0" />
                : <User size={14} className="text-slate-400 shrink-0" />}
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{a.name || "(sin nombre)"}</p>
                <p className="text-[10px] text-slate-500">
                  DNI {a.id_number || "-"} · {a.position || "-"}
                  {a.employee_id && <span className="text-emerald-700 font-bold"> · Vinculado</span>}
                </p>
              </div>
              {a.signed && <span className="text-[10px] font-bold text-emerald-700">FIRMÓ</span>}
            </div>
          ))}
        </div>
      </div>
      {it.created_at && (
        <p className="text-[10px] text-slate-400 flex items-center gap-1">
          <ClockCounterClockwise size={11} /> Registrado {it.created_at.slice(0, 16).replace("T", " ")}
          {it.created_by && ` por ${it.created_by}`}
        </p>
      )}
      {onEdit && (
        <Button onClick={onEdit} className="w-full bg-blue-900 hover:bg-blue-800 gap-1.5"
                data-testid="toolbox-detail-edit-btn">
          <PencilSimple size={14} weight="bold" /> Editar esta charla
        </Button>
      )}
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="overline-label">{label}</Label>
      <p className="mt-1 font-semibold text-slate-900">{value || "—"}</p>
    </div>
  );
}

function ToolboxForm({ form, patchForm, addAtt, linkEmp, unlinkEmp, save, mode }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="overline-label">Tema</Label>
          <Input data-testid="toolbox-topic" value={form.topic}
                  onChange={(e) => patchForm({ topic: e.target.value })} />
        </div>
        <div>
          <Label className="overline-label">Fecha</Label>
          <DateField testid="toolbox-date" value={form.date}
                      onChange={(v) => patchForm({ date: v })} />
        </div>
        <ProjectField value={form.project_id} onChange={(v) => patchForm({ project_id: v })} testid="toolbox-project" />
        <div>
          <Label className="overline-label">Presentador</Label>
          <Input data-testid="toolbox-presenter" value={form.presenter}
                  onChange={(e) => patchForm({ presenter: e.target.value })} />
        </div>
        <div>
          <Label className="overline-label">Área</Label>
          <Input data-testid="toolbox-area" value={form.area}
                  onChange={(e) => patchForm({ area: e.target.value })} />
        </div>
        <div>
          <Label className="overline-label">Duración (min)</Label>
          <Input data-testid="toolbox-duration" type="number" min={1} max={60}
                  value={form.duration_min}
                  onChange={(e) => patchForm({ duration_min: Number(e.target.value) })} />
        </div>
        <div className="col-span-2">
          <Label className="overline-label">Puntos clave</Label>
          <Textarea rows={3} data-testid="toolbox-key-points" value={form.key_points}
                     onChange={(e) => patchForm({ key_points: e.target.value })} />
        </div>
        <div className="col-span-2 border-t border-slate-100 pt-3">
          <p className="overline-label mb-2">Fotos de la charla (asistencia, pizarra, área)</p>
          <PhotoUploader
            value={form.photos || []}
            onChange={(photos) => patchForm({ photos })}
            max={6}
            testidPrefix="toolbox-photo"
          />
        </div>
        <div className="col-span-2 border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="overline-label">Asistentes ({(form.attendees || []).length})</p>
            <Button size="sm" variant="ghost" onClick={addAtt} data-testid="toolbox-add-attendee"
                     className="h-7 text-xs text-blue-900">
              <Plus size={12} /> Añadir
            </Button>
          </div>
          <p className="text-[10px] text-slate-500 mb-1 italic">
            Vincula a nómina para trazabilidad. Visitantes o externos pueden escribirse manualmente.
          </p>
          <div className="space-y-1.5">
            {(form.attendees || []).map((a, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 items-start"
                    data-testid={`toolbox-att-row-${idx}`}>
                {a.employee_id ? (
                  <div className="col-span-11 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
                        data-testid={`toolbox-att-linked-${idx}`}>
                    <IdentificationBadge size={16} weight="fill" className="text-emerald-700 shrink-0" />
                    <div className="flex-1 min-w-0 text-xs">
                      <p className="font-semibold text-slate-900 truncate">{a.name || "(nombre pendiente)"}</p>
                      <p className="text-[10px] text-slate-500 truncate">
                        DNI {a.id_number || "-"} · {a.position || "-"} · <span className="text-emerald-700 font-bold">Vinculado a nómina</span>
                      </p>
                    </div>
                    <button
                      onClick={() => unlinkEmp(idx)}
                      className="text-[10px] text-slate-500 hover:text-red-600 font-semibold whitespace-nowrap"
                      data-testid={`toolbox-att-unlink-${idx}`}
                      title="Desvincular y editar manualmente"
                    >Editar</button>
                  </div>
                ) : (
                  <>
                    <div className="col-span-5">
                      <EmployeePicker
                        onPick={(emp) => linkEmp(idx, emp)}
                        placeholder="Buscar en nómina o escribir manual →"
                        testid={`toolbox-att-picker-${idx}`}
                      />
                    </div>
                    <Input className="col-span-3 h-8 text-xs" placeholder="Nombre manual" value={a.name}
                            onChange={(e) => { const n=[...form.attendees]; n[idx]={...n[idx],name:e.target.value}; patchForm({attendees:n}); }}
                            data-testid={`toolbox-att-name-${idx}`} />
                    <Input className="col-span-2 h-8 text-xs" placeholder="DNI" value={a.id_number}
                            onChange={(e) => { const n=[...form.attendees]; n[idx]={...n[idx],id_number:e.target.value}; patchForm({attendees:n}); }} />
                    <Input className="col-span-1 h-8 text-xs" placeholder="Cargo" value={a.position}
                            onChange={(e) => { const n=[...form.attendees]; n[idx]={...n[idx],position:e.target.value}; patchForm({attendees:n}); }} />
                  </>
                )}
                <button className="col-span-1 h-8 text-slate-400 hover:text-red-600 flex items-center justify-center"
                         onClick={() => patchForm({ attendees: form.attendees.filter((_, i) => i !== idx) })}
                         data-testid={`toolbox-att-remove-${idx}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Button data-testid="toolbox-save" onClick={save}
               disabled={!form.topic || !form.date}
               className="bg-blue-900 hover:bg-blue-800 w-full">
        {mode === "edit" ? "Guardar cambios" : "Registrar"}
      </Button>
    </>
  );
}
